import React, { useState, useEffect } from 'react';
import { Users, Building2, UserCheck, CreditCard, Wallet, TrendingUp, Trash2, RefreshCw, Shield, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = getAuthHeader();
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
    } catch (err) {
      console.error('Admin fetch error:', err);
      if (err.response?.status === 403) {
        setError('You do not have admin access');
      } else if (err.response?.status === 401) {
        setError('Please log in to access admin panel');
      } else {
        setError('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`${t('admin.confirmDelete')} (${email})`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeader() });
      toast.success(t('admin.userDeleted'));
      fetchData();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-6 px-3 md:px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen py-6 px-3 md:px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-6 h-6 text-primary" />
              <h1 className="text-2xl md:text-3xl font-bold text-white">{t('admin.title')}</h1>
            </div>
            <p className="text-white/60 text-sm">{t('admin.subtitle')}</p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-primary" />
                <span className="text-white/60 text-xs">{t('admin.totalUsers')}</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.total_users}</div>
              <div className="text-xs text-green-500 mt-1">+{stats.recent_users?.length || 0} recent</div>
            </div>

            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <UserCheck className="w-5 h-5 text-green-500" />
                <span className="text-white/60 text-xs">{t('admin.verifiedUsers')}</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.kyc_verified_users}</div>
              <div className="text-xs text-white/60 mt-1">of {stats.total_users} users</div>
            </div>

            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-5 h-5 text-yellow-500" />
                <span className="text-white/60 text-xs">Wallets Created</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.total_wallets}</div>
              <div className="text-xs text-white/60 mt-1">{stats.two_fa_enabled_users} with 2FA</div>
            </div>

            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                <span className="text-white/60 text-xs">Bank Accounts</span>
              </div>
              <div className="text-2xl font-bold text-white">{stats.total_bank_accounts}</div>
              <div className="text-xs text-white/60 mt-1">accounts opened</div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h2 className="text-lg font-semibold text-white">{t('admin.userList')}</h2>
            <p className="text-white/60 text-sm">{users.length} {t('admin.totalUsers').toLowerCase()}</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left text-xs text-white/60 font-medium px-4 py-3">{t('admin.name')}</th>
                  <th className="text-left text-xs text-white/60 font-medium px-4 py-3">{t('admin.email')}</th>
                  <th className="text-left text-xs text-white/60 font-medium px-4 py-3">{t('admin.type')}</th>
                  <th className="text-left text-xs text-white/60 font-medium px-4 py-3">{t('admin.status')}</th>
                  <th className="text-left text-xs text-white/60 font-medium px-4 py-3">{t('admin.joined')}</th>
                  <th className="text-left text-xs text-white/60 font-medium px-4 py-3">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                          <span className="text-primary text-sm font-medium">
                            {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <span className="text-white text-sm font-medium">{u.full_name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white/80 text-sm">{u.email}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.account_type === 'business' 
                          ? 'bg-blue-500/20 text-blue-400' 
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {u.account_type || 'personal'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.kyc_verified ? (
                        <span className="text-green-500 text-sm">✓ {t('admin.verified')}</span>
                      ) : (
                        <span className="text-white/40 text-sm">{t('admin.pending')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white/60 text-sm">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.email !== user?.email && (
                        <button
                          onClick={() => handleDeleteUser(u.id, u.email)}
                          className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-white/40">
                      No users registered yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
